/**
 * PFR turg'un rejim — hajm bo'ylab RK4 integratsiya.
 *
 * TENGLAMALAR (V — kirishdan hisoblangan hajm, 0 … V_R)
 *   dC_A/dV = −(−r_A)/F
 *   dT/dV   = [(−ΔH_r)·(−r_A) − U·(4/d)·(T − Tj)] / (F·ρ·cp)      (isothermal: 0)
 *   dQ_rem/dV = U·(4/d)·(T − Tj)                                  (balans tekshiruvi uchun)
 *   C_B = C_B0 + (b/a)·(C_A0 − C_A);  X = 1 − C_A/C_A0;  τ = V_R/F
 *
 * ANALITIK CHEGARALAR (test): n = 1 izotermik X = 1 − e^{−kτ};
 *   n = 2: X = kτC_A0/(1 + kτC_A0); adiabatik: T = Tin + ΔT_ad·X (har nuqtada).
 *
 * SONLI SXEMA: qat'iy qadamli RK4 (solvers/rk4Step). Qadamlar soni
 *   N = max(integrationSteps, V_R/ΔV_stab), ΔV_stab = 0.25·min(F/(n·k_max·C_A0^(n−1)), F·ρ·cp/(U·4/d)),
 *   k_max = k(Tin + max(0, ΔT_ad)) — eng yomon holat; N ≤ 200000.
 * Har qadamdan keyin C_A ≥ 0 proyeksiyasi. Barcha qadamlar yozib olinadi, so'ng
 * `profilePoints` ga siyraklashtiriladi; hot spot to'liq to'plamdan olinadi.
 *
 * QABUL QILINGAN SODDALASHTIRISHLAR (ASSUMPTION)
 *   1. Ideal siqib chiqarish: radial gradient yo'q, o'q bo'ylab dispersiya yo'q.
 *   2. Bosim yo'qotishi yo'q; suyuq faza, ρ·cp doimiy; hajm o'zgarmas.
 *   3. Tj butun uzunlik bo'ylab o'zgarmas (ko'ylakdagi sovutgich harorati doimiy), U doimiy.
 *   4. Isothermal rejimda kerakli issiqlik lokal cheksiz tez chiqariladi.
 *   5. Kinetika farazlari — reaction/kinetics.ts.
 *
 * MANBA: Levenspiel CRE 5-bob (PFR), 9-bob; Fogler ECRE 12-bob (PFR issiqlik effektlari,
 *        hot spot); Kasatkin (quvurli reaktorlar).
 */
import { rk4Step } from '../../solvers';
import { warn, type SteadyResult, type Warning } from '../../types';
import {
  adiabaticRise,
  arrhenius,
  damkohler,
  mixtureProperties,
  rateDerivative,
  reactionRate,
  validateReaction,
  type ReactionMixture,
} from '../reaction';
import type { PfrInputs, PfrOutputs, PfrParams, ProfilePoint } from './types';

export const DEFAULT_PROFILE_POINTS = 50;
export const DEFAULT_STEPS = 500;
const MAX_STEPS = 200_000;

export function validatePfrParams(p: PfrParams): void {
  if (!(p.volume > 0)) throw new Error('pfr: volume must be > 0');
  if (!(p.diameter > 0)) throw new Error('pfr: diameter must be > 0');
  validateReaction('pfr', p.reaction);
  if (p.mode === 'jacketed' && !(p.U !== undefined && p.U >= 0))
    throw new Error('pfr: U is required (≥ 0) for jacketed');
  if (p.Tmax !== undefined && !(p.Tmax > 0)) throw new Error('pfr: Tmax must be > 0');
  if (p.profilePoints !== undefined && !(p.profilePoints >= 2))
    throw new Error('pfr: profilePoints must be ≥ 2');
  if (p.integrationSteps !== undefined && !(p.integrationSteps >= 1))
    throw new Error('pfr: integrationSteps must be ≥ 1');
}

export function validatePfrInputs(p: PfrParams, i: PfrInputs): void {
  if (!(i.flowRate >= 0)) throw new Error('pfr: flowRate must be ≥ 0');
  if (!(i.CA0 >= 0)) throw new Error('pfr: CA0 must be ≥ 0');
  if (i.CB0 !== undefined && !(i.CB0 >= 0)) throw new Error('pfr: CB0 must be ≥ 0');
  if (!(i.Tin > 0)) throw new Error('pfr: Tin must be > 0 K');
  if (p.mode === 'jacketed' && !(i.Tjacket !== undefined && i.Tjacket > 0))
    throw new Error('pfr: Tjacket is required (> 0 K) for jacketed');
}

/** Solishtirma issiqlik uzatish: U·(4/d), W/(m³·K) (jacketed bo'lmasa 0) */
export function wallCoefficient(p: PfrParams): number {
  return p.mode === 'jacketed' ? ((p.U as number) * 4) / p.diameter : 0;
}

function point(V: number, CA: number, T: number, CA0: number, k0: number, Ea: number, n: number) {
  return {
    V,
    CA,
    T,
    X: CA0 > 0 ? 1 - CA / CA0 : 0,
    rate: reactionRate(arrhenius(k0, Ea, T), CA, n),
  };
}

/** Uzun to'plamdan ≈ m nuqta (birinchi va oxirgi saqlanadi) */
function thin(points: ProfilePoint[], m: number): ProfilePoint[] {
  if (points.length <= m) return points;
  const out: ProfilePoint[] = [];
  for (let i = 0; i < m; i++) {
    const idx = Math.round((i * (points.length - 1)) / (m - 1));
    out.push(points[idx] as ProfilePoint);
  }
  return out;
}

function noFlowResult(
  p: PfrParams,
  i: PfrInputs,
  mix: ReactionMixture,
  warnings: Warning[],
): SteadyResult<PfrOutputs> {
  warn(warnings, 'reactor.noFlow', 'critical');
  const { k0, Ea } = p.reaction;
  const complete = k0 > 0 && i.CA0 > 0;
  const CA = complete ? 0 : i.CA0;
  const Tj = p.mode === 'jacketed' ? (i.Tjacket as number) : i.Tin;
  const dTad = adiabaticRise(mix, i.CA0);
  const T =
    p.mode === 'adiabatic' ? i.Tin + (complete ? dTad : 0) : p.mode === 'jacketed' ? Tj : i.Tin;
  const profile = [
    point(0, i.CA0, i.Tin, i.CA0, k0, Ea, mix.order),
    point(p.volume, CA, T, i.CA0, k0, Ea, mix.order),
  ];
  return {
    outputs: {
      conversion: complete ? 1 : 0,
      CA,
      CB: (i.CB0 ?? 0) + mix.yieldB * (i.CA0 - CA),
      T,
      rate: 0,
      k: arrhenius(k0, Ea, T),
      residenceTime: Infinity,
      damkohler: complete ? Infinity : 0,
      adiabaticRise: dTad,
      heatGenerated: 0,
      heatRemoved: 0,
      Tmax: Math.max(T, i.Tin),
      TmaxPosition: T >= i.Tin ? p.volume : 0,
      profile,
      density: mix.density,
      cp: mix.cp,
    },
    warnings,
    diagnostics: { converged: true, iterations: 0, residual: 0 },
  };
}

export function solveSteady(p: PfrParams, inputs: PfrInputs): SteadyResult<PfrOutputs> {
  validatePfrParams(p);
  validatePfrInputs(p, inputs);
  const warnings: Warning[] = [];
  const mix = mixtureProperties(p.reaction, inputs.Tin, warnings);
  const { flowRate: F, CA0, Tin } = inputs;
  if (F <= 0) return noFlowResult(p, inputs, mix, warnings);

  const { k0, Ea } = p.reaction;
  const n = mix.order;
  const Ua = wallCoefficient(p);
  const Tj = p.mode === 'jacketed' ? (inputs.Tjacket as number) : Tin;
  const isothermal = p.mode === 'isothermal';
  const dTad = adiabaticRise(mix, CA0);
  const FrhoCp = F * mix.rhoCp;

  // Qadam soni: aniqlik (integrationSteps) va barqarorlik
  const kMax = arrhenius(k0, Ea, Tin + Math.max(0, isothermal ? 0 : dTad));
  const dC = rateDerivative(kMax, Math.max(CA0, 1e-300), n);
  let dVstab = Infinity;
  if (dC > 0) dVstab = Math.min(dVstab, F / dC);
  if (Ua > 0) dVstab = Math.min(dVstab, FrhoCp / Ua);
  const nStab = Number.isFinite(dVstab) ? Math.ceil(p.volume / (0.25 * dVstab)) : 1;
  const N = Math.min(MAX_STEPS, Math.max(p.integrationSteps ?? DEFAULT_STEPS, nStab));
  const dV = p.volume / N;

  // y = [C_A, T, Q_rem]
  const deriv = (_V: number, y: readonly number[]): number[] => {
    const CA = y[0] as number;
    const T = y[1] as number;
    const r = reactionRate(arrhenius(k0, Ea, T), CA, n);
    const qWall = Ua * (T - Tj);
    const dT = isothermal ? 0 : (mix.heatRelease * r - qWall) / FrhoCp;
    return [-r / F, dT, qWall];
  };

  const all: ProfilePoint[] = [point(0, CA0, Tin, CA0, k0, Ea, n)];
  let y = [CA0, Tin, 0];
  let V = 0;
  let Tmax = Tin;
  let TmaxPosition = 0;
  for (let i = 0; i < N; i++) {
    y = rk4Step(deriv, V, y, dV);
    V = (i + 1) * dV;
    const CAi = Math.max(0, y[0] as number);
    y[0] = CAi;
    if (isothermal) y[1] = Tin;
    const T = y[1] as number;
    if (!Number.isFinite(T)) throw new Error('pfr: non-finite temperature (unstable integration)');
    if (T > Tmax) {
      Tmax = T;
      TmaxPosition = V;
    }
    all.push(point(V, CAi, T, CA0, k0, Ea, n));
  }

  const CA = y[0] as number;
  const Tout = y[1] as number;
  const kOut = arrhenius(k0, Ea, Tout);
  const tau = p.volume / F;
  const heatGenerated = mix.heatRelease * F * (CA0 - CA);
  const heatRemoved = isothermal ? heatGenerated : p.mode === 'jacketed' ? (y[2] as number) : 0;
  const conversion = CA0 > 0 ? 1 - CA / CA0 : 0;

  if (conversion < 1e-6) warn(warnings, 'reactor.noReaction', 'info');
  if (p.Tmax !== undefined && Tmax > p.Tmax) {
    warn(warnings, 'reactor.hotSpot', 'warning', {
      T: Number(Tmax.toFixed(1)),
      Tmax: p.Tmax,
      V: Number(TmaxPosition.toPrecision(4)),
    });
  }

  return {
    outputs: {
      conversion,
      CA,
      CB: (inputs.CB0 ?? 0) + mix.yieldB * (CA0 - CA),
      T: Tout,
      rate: reactionRate(kOut, CA, n),
      k: kOut,
      residenceTime: tau,
      damkohler: damkohler(arrhenius(k0, Ea, Tin), tau, CA0, n),
      adiabaticRise: dTad,
      heatGenerated,
      heatRemoved,
      Tmax,
      TmaxPosition,
      profile: thin(all, p.profilePoints ?? DEFAULT_PROFILE_POINTS),
      density: mix.density,
      cp: mix.cp,
    },
    warnings,
    diagnostics: { converged: true, iterations: N, residual: 0 },
  };
}
